import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import PageHeader from '@Guidelines/Layout/PageHeader';
import BeneficiaryContactsContent from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/BeneficiaryContactsContent';
import BeneficiaryContactsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/BeneficiaryContactsNotifications';
import CreateContactButton from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/CreateContactButton';
import BeneficiaryContactsConfirmationDialogs from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/ConfirmationDialogs/BeneficiaryContactsConfirmationDialogs';
import UpsertContactSidebar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/Sidebars/UpsertContactSidebar';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const contactsItems = [{ label: 'Contacts' }] as const;

const containerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	flex: 1;
	overflow: hidden;
	min-height: 0;
`;

const subContainerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	overflow-y: auto;
`;

/**
 * Assembles the screen: breadcrumb, header, the data-owning `BeneficiaryContactsContent`, the
 * upsert sidebar, the confirmation dialogs and notifications. Redirects to the beneficiary list when the
 * route's `beneficiary` segment is not a valid id, and only mounts `BeneficiaryContactsContent` once
 * it is — the content never has to consider an invalid id itself.
 */
const BeneficiaryContacts: React.FC = () => {
	const navigate = useNavigate();
	const params = useParams<{ beneficiary: string }>();
	const beneficiaryId = params.beneficiary !== undefined ? Number(params.beneficiary) : Number.NaN;
	const isInvalidBeneficiaryId = Number.isNaN(beneficiaryId);

	React.useEffect(() => {
		if (isInvalidBeneficiaryId) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
		}
	}, [isInvalidBeneficiaryId, navigate]);

	return (
		<div css={containerStyle}>
			{!isInvalidBeneficiaryId && (
				<SubjectBreadcrumb
					subject={SubjectAddress.forBeneficiary(beneficiaryId)}
					items={contactsItems}
				/>
			)}
			<div css={subContainerStyle}>
				<PageHeader
					eyebrow="BÉNÉFICIAIRES"
					title="Contacts du dossier"
					description="L'entourage de la personne accompagnée : proche aidant, personne de confiance et autres contacts utiles au suivi."
					actions={
						<CreateContactButton beneficiaryId={isInvalidBeneficiaryId ? null : beneficiaryId} />
					}
				/>
				{!isInvalidBeneficiaryId && <BeneficiaryContactsContent beneficiaryId={beneficiaryId} />}
			</div>
			<UpsertContactSidebar />
			<BeneficiaryContactsConfirmationDialogs />
			<BeneficiaryContactsNotifications />
		</div>
	);
};

export default BeneficiaryContacts;
